import { useMemo } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { useTrackerCheck } from '../../api/hooks/usePortfolioTracker'
import type { TrackerCheckRequest, TrackerRevisionStrategy } from '../../api/types/portfolio-tracker.types'
import { StrategyDirection } from '../../api/types/trading-strategy.types'
import routePaths from '../../routes/route-paths'
import { formatDateShort } from '../../utils/formatters'
import { BUILDER_MOST_STRATEGIES, formatMoney, formatPercent, symbolOf } from './portfolio-tracker'

const DAY = /^\d{4}-\d{2}-\d{2}$/
const DEFAULT_AMOUNT = 100_000

/**
 * A setup as its address carries it. Even is one of the builder's portfolios; own gives each strategy's percent,
 * and amount gives each strategy's dollars.
 */
export interface TrackerSetup {
  revisionId: string
  side: StrategyDirection
  split: 'even' | 'own' | 'amount'
  /** By amount, what the strategies' amounts add up to. */
  amount: number
  start: string
  strategies: { key: string; value: number | null }[]
}

/** What a query names of a setup, any of it missing or unreadable. */
export type PartialSetup = Partial<Omit<TrackerSetup, 'strategies'>> & Pick<TrackerSetup, 'strategies'>

/** How step 2 splits the money: each strategy's percent of the amount, or each strategy's own amount. */
export type TrackerSplit = 'percent' | 'amount'

/** A strategy's row in step 2, as typed. */
export interface DraftRow {
  key: string
  percentText: string
  amountText: string
}

/** Step 2 as staff have it so far. */
export interface Draft {
  split: TrackerSplit
  amountText: string
  start: string
  /** Typed in, so picking other strategies keeps their figures rather than splitting evenly again. */
  custom: boolean
  rows: DraftRow[]
}

/** An i18n key under setup, and what it names. */
export interface Message {
  key: string
  values?: Record<string, string | number>
}

export interface DraftCheck {
  ok: boolean
  amount: Message | null
  start: Message | null
  rows: Record<string, Message>
  total: { tone: 'ok' | 'warn' | 'err'; message: Message }
  /** Of the allocations by percent, or of the amounts by amount. */
  sum: number
}

const isPositive = (value: number) => Number.isFinite(value) && value > 0

/**
 * Each weight's share of their total, in percent to `places` decimal places, adding up to exactly 100. The
 * largest remainders take the spare units.
 */
function sharesOf(weights: number[], places: number): number[] {
  const scale = 10 ** places
  const whole = 100 * scale
  const total = weights.reduce((sum, weight) => sum + weight, 0)
  if (!weights.length || total <= 0) return weights.map(() => 0)

  const raw = weights.map((weight) => (weight / total) * whole)
  const base = raw.map(Math.floor)
  let spare = whole - base.reduce((sum, units) => sum + units, 0)
  raw
    .map((value, index) => [value - base[index], index] as const)
    .sort((a, b) => b[0] - a[0])
    .forEach(([, index]) => {
      if (spare > 0) {
        base[index]++
        spare--
      }
    })
  return base.map((units) => units / scale)
}

/** The amount boxes read in thousands, as the portfolio builder's does: "100", "2.5". */
export const formatThousands = (thousands: number): string =>
  // To three places, the dollar, so an amount of $12,750 reads back as itself.
  thousands.toLocaleString('en-US', { maximumFractionDigits: 3 })

/** What an amount box holds, in thousands. Null for anything that isn't an amount above zero. */
export function parseThousands(typed: string): number | null {
  const cleaned = typed.trim().replace(/[$,\s]/g, '')
  const value = Number(cleaned)
  return cleaned !== '' && Number.isFinite(value) && value > 0 ? value : null
}

/** The dollars an amount box holds: "100" is $100,000. */
export function parseAmount(text: string): number | null {
  const thousands = parseThousands(text)
  return thousands === null ? null : Math.round(thousands * 1000 * 100) / 100
}

/** What an amount box shows for a dollar amount: $100,000 is "100". */
export const formatAmountText = (amount: number): string => formatThousands(amount / 1000)

const formatShareText = (percent: number): string => String(Math.round(percent * 10) / 10)

export const rowPercent = (row: DraftRow): number => (row.percentText.trim() === '' ? NaN : Number(row.percentText))
export const rowAmount = (row: DraftRow): number => parseAmount(row.amountText) ?? NaN

/** By amount, the sum of the amounts; by percent, the amount typed. */
export function totalOf(draft: Draft): number | null {
  if (draft.split === 'amount') {
    return draft.rows.reduce((sum, row) => sum + (isPositive(rowAmount(row)) ? rowAmount(row) : 0), 0)
  }
  return parseAmount(draft.amountText)
}

/**
 * Even shares of the total, and the dollars that come to. Each amount is a whole $100, so it reads as one place
 * of thousands; the first strategy takes what is left over.
 */
export function evenRows(keys: string[], draft: Draft): DraftRow[] {
  const shares = sharesOf(
    keys.map(() => 1),
    1,
  )
  const total = totalOf(draft) || DEFAULT_AMOUNT
  const each = Math.floor(total / Math.max(1, keys.length) / 100) * 100

  return keys.map((key, index) => ({
    key,
    percentText: formatShareText(shares[index]),
    amountText: formatAmountText(index === 0 ? total - each * (keys.length - 1) : each),
  }))
}

/** The rows for the strategies picked, in the order step 1 lists them. */
export function withStrategies(draft: Draft, keys: string[]): Draft {
  if (!draft.custom) return { ...draft, rows: evenRows(keys, draft) }

  const kept = new Map(draft.rows.map((row) => [row.key, row]))
  return { ...draft, rows: keys.map((key) => kept.get(key) ?? { key, percentText: '', amountText: '' }) }
}

/** Switching carries the split across: percents become dollars of the total, and dollars become shares of theirs. */
export function withSplit(draft: Draft, split: TrackerSplit): Draft {
  if (draft.split === split) return draft

  if (split === 'amount') {
    const amount = parseAmount(draft.amountText)
    return {
      ...draft,
      split,
      rows: draft.rows.map((row) => {
        const percent = rowPercent(row)
        return {
          ...row,
          amountText: amount !== null && isPositive(percent) ? formatAmountText(Math.round((amount * percent) / 100)) : '',
        }
      }),
    }
  }

  const total = totalOf(draft) ?? 0
  if (total <= 0) return { ...draft, split }

  const shares = sharesOf(
    draft.rows.map((row) => (isPositive(rowAmount(row)) ? rowAmount(row) : 0)),
    1,
  )
  return {
    ...draft,
    split,
    amountText: formatAmountText(total),
    rows: draft.rows.map((row, index) => ({
      ...row,
      percentText: isPositive(rowAmount(row)) ? formatShareText(shares[index]) : '',
    })),
  }
}

/** The steps filled in from what the address names, or a first visit: $100k, split evenly. */
export function draftFrom(partial: PartialSetup): Draft {
  const byAmount = partial.split === 'amount'
  const custom = partial.split === 'own' || byAmount
  const amount = byAmount
    ? partial.strategies.reduce((sum, strategy) => sum + (strategy.value ?? 0), 0)
    : (partial.amount ?? DEFAULT_AMOUNT)
  const draft: Draft = {
    split: byAmount ? 'amount' : 'percent',
    amountText: formatAmountText(amount || DEFAULT_AMOUNT),
    start: partial.start ?? '',
    custom,
    rows: [],
  }
  const keys = partial.strategies.map((strategy) => strategy.key)
  if (!custom) return { ...draft, rows: evenRows(keys, draft) }

  return {
    ...draft,
    rows: partial.strategies.map((strategy) => ({
      key: strategy.key,
      percentText: !byAmount && strategy.value !== null ? String(strategy.value) : '',
      amountText: byAmount && strategy.value !== null ? formatAmountText(strategy.value) : '',
    })),
  }
}

/** What stops the portfolio being checked, field by field, and the line under the split. */
export function checkDraft(
  draft: Draft,
  names: Map<string, string>,
  firstSignal: string | null,
  asOf: string,
): DraftCheck {
  const byAmount = draft.split === 'amount'
  const badDay = !draft.start || (firstSignal !== null && draft.start < firstSignal) || draft.start > asOf

  const amount = !byAmount && parseAmount(draft.amountText) === null ? { key: 'errors.amount' } : null
  const start: Message | null = !badDay
    ? null
    : firstSignal
      ? { key: 'errors.dateRange', values: { from: formatDateShort(firstSignal), to: formatDateShort(asOf) } }
      : { key: 'errors.dateBy', values: { to: formatDateShort(asOf) } }

  const rows: Record<string, Message> = {}
  let sum = 0
  for (const row of draft.rows) {
    const name = names.get(row.key) ?? ''
    const value = byAmount ? rowAmount(row) : rowPercent(row)
    if (!isPositive(value)) {
      rows[row.key] = { key: byAmount ? 'errors.rowAmount' : 'errors.rowPercent', values: { name } }
    }
    sum += Number.isFinite(value) && (byAmount ? value > 0 : true) ? value : 0
  }
  sum = Math.round(sum * 100) / 100

  const typedAmount = parseAmount(draft.amountText)
  const count = draft.rows.length
  const total: DraftCheck['total'] = !count
    ? { tone: 'err', message: { key: 'total.none' } }
    : byAmount
      ? { tone: 'ok', message: { key: 'total.amounts', values: { count } } }
      : sum > 100
        ? { tone: 'err', message: { key: 'total.over', values: { sum: formatPercent(sum) } } }
        : sum < 100
          ? {
              tone: 'warn',
              message: typedAmount
                ? {
                    key: 'total.cashWithAmount',
                    values: { rest: formatPercent(100 - sum), amount: formatMoney((typedAmount * (100 - sum)) / 100) },
                  }
                : { key: 'total.cash', values: { rest: formatPercent(100 - sum) } },
            }
          : { tone: 'ok', message: { key: 'total.all' } }

  const ok = !amount && !start && Object.keys(rows).length === 0 && total.tone !== 'err'
  return { ok, amount, start, rows, total, sum }
}

/** An even split of up to six strategies, one per symbol, is one of the builder's own portfolios. */
export function isBuilderPortfolio(draft: Draft, strategies: TrackerRevisionStrategy[]): boolean {
  if (draft.custom || draft.rows.length === 0 || draft.rows.length > BUILDER_MOST_STRATEGIES) return false

  const byKey = new Map(strategies.map((strategy) => [strategy.key, strategy]))
  const symbols = new Set(
    draft.rows.map((row) => {
      const strategy = byKey.get(row.key)
      return strategy ? symbolOf(strategy) : `key:${row.key}`
    }),
  )
  return symbols.size === draft.rows.length
}

export function toSetup(
  draft: Draft,
  revisionId: string,
  side: StrategyDirection,
  builderPortfolio: boolean,
): TrackerSetup {
  const byAmount = draft.split === 'amount'
  const split = builderPortfolio ? 'even' : byAmount ? 'amount' : 'own'
  return {
    revisionId,
    side,
    split,
    amount: totalOf(draft) ?? 0,
    start: draft.start,
    strategies: draft.rows.map((row) => ({
      key: row.key,
      value: split === 'even' ? null : byAmount ? rowAmount(row) : rowPercent(row),
    })),
  }
}

/**
 * A setup as the results pages carry it, so a refresh, Back, or a signal page and back keeps it:
 * ?revision=…&side=3&split=even&amount=100000&start=2025-09-02&s=7&s=18. An own split gives each percent (s=7:20);
 * by amount gives each strategy's dollars (s=7:25000) and no total.
 */
export function setupQuery(setup: TrackerSetup): string {
  const params = new URLSearchParams()
  params.set('revision', setup.revisionId)
  params.set('side', String(setup.side))
  params.set('split', setup.split)
  if (setup.split !== 'amount') params.set('amount', String(setup.amount))
  params.set('start', setup.start)
  setup.strategies.forEach((strategy) =>
    params.append('s', strategy.value === null ? strategy.key : `${strategy.key}:${strategy.value}`),
  )
  return `?${params}`
}

/** The check a whole setup asks for. By amount, each strategy's share of the total, to a millionth of a percent. */
function toCheckRequest(setup: TrackerSetup): TrackerCheckRequest {
  const values = setup.strategies.map((strategy) => strategy.value ?? 0)
  const allocations =
    setup.split === 'even' ? setup.strategies.map(() => null) : setup.split === 'amount' ? sharesOf(values, 6) : values

  return {
    revisionId: setup.revisionId,
    side: setup.side,
    amount: setup.amount,
    start: setup.start,
    evenSplit: setup.split === 'even',
    strategies: setup.strategies.map((strategy, index) => ({
      key: strategy.key,
      allocationPercent: allocations[index],
    })),
  }
}

export function readSetup(search: string): { partial: PartialSetup; complete: TrackerCheckRequest | null } {
  const params = new URLSearchParams(search)
  const sideNumber = Number(params.get('side'))
  const side = [StrategyDirection.LongAndShort, StrategyDirection.LongOnly].find((each) => each === sideNumber)
  const splitText = params.get('split')
  const split = splitText === 'even' || splitText === 'own' || splitText === 'amount' ? splitText : undefined
  const start = params.get('start') ?? ''

  // An own split's or an amount's key carries its figure after the last colon; an even split's is the key alone.
  const strategies = params.getAll('s').map((text) => {
    const colon = text.lastIndexOf(':')
    if (split === 'even' || colon < 0) return { key: text, value: null }
    const value = Number(text.slice(colon + 1))
    return { key: text.slice(0, colon), value: Number.isFinite(value) ? value : null }
  })

  const amount =
    split === 'amount'
      ? strategies.reduce((sum, strategy) => sum + (strategy.value ?? 0), 0)
      : Number(params.get('amount'))

  const partial: PartialSetup = {
    revisionId: params.get('revision') || undefined,
    side,
    split,
    amount: amount > 0 ? amount : undefined,
    start: DAY.test(start) ? start : undefined,
    strategies,
  }

  const valued = split === 'even' || strategies.every((strategy) => (strategy.value ?? 0) > 0)
  const complete =
    partial.revisionId &&
    partial.side !== undefined &&
    partial.split !== undefined &&
    partial.amount !== undefined &&
    partial.start &&
    strategies.length > 0 &&
    valued
      ? toCheckRequest({
          revisionId: partial.revisionId,
          side: partial.side,
          split: partial.split,
          amount: partial.amount,
          start: partial.start,
          strategies,
        })
      : null

  return { partial, complete }
}

/** The setup the current page's query names, and the query itself to carry on to the next page. */
export function useTrackerSetup() {
  const { search } = useLocation()
  const read = useMemo(() => readSetup(search), [search])
  return { ...read, query: search }
}

/** On a strategy page, that strategy's name once the portfolio has been worked out; the breadcrumb shows it. */
export function useTrackerStrategyName(): string | undefined {
  const { pathname } = useLocation()
  const { complete } = useTrackerSetup()
  const match = matchPath(routePaths.PortfolioTrackerStrategy, pathname)
  const check = useTrackerCheck(match ? complete : null)
  // matchPath leaves the key as the address has it; the page's own params come decoded.
  const key = match?.params.key ? decodeURIComponent(match.params.key) : undefined
  return key ? check.data?.strategies.find((strategy) => strategy.key === key)?.name : undefined
}

/** Which step the setup page opens on: Change setup comes back to step 2. */
export interface SetupPageState {
  step: 1 | 2
}

export const resultsPath = (query: string) => `${routePaths.PortfolioTrackerResults}${query}`

export const differencesPath = (query: string) => `${routePaths.PortfolioTrackerDifferences}${query}`

export const strategyPath = (key: string, query: string) =>
  `${routePaths.PortfolioTrackerStrategy.replace(':key', encodeURIComponent(key))}${query}`

export const setupPath = (query: string) => `${routePaths.PortfolioTracker}${query}`
